import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { RECORD_RESEARCH_FEEDBACK, RESEARCH_ASK } from '../../graphql';
import { ResearchAskResponse, ResearchHit } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';

/**
 * Prism AI → Research ("tinycms UX optimization review v6", Prototype v6): ask
 * the archive a question (answers cite the articles they came from), with
 * starter questions under the box; below, the latest facts, filterable by
 * category. Only atomized articles feed it, so nothing here is new reporting.
 */

const RESEARCH_OVERVIEW = gql`
  query ResearchOverview($accountId: ID!, $category: String, $limit: Int) {
    researchOverview(accountId: $accountId, category: $category, limit: $limit) {
      articleCount
      atomizedCount
      factCount
      categories
      suggestions
      facts {
        fact
        date
        publishedDate
        topicId
        topicTitle
        tags
      }
    }
  }
`;

// Only whether Research is set up and on; never the keys or where it runs.
const RESEARCH_AVAILABLE = gql`
  query ResearchAvailable($accountId: ID!) {
    researchSettings(accountId: $accountId) {
      id
      searchApiKey
      isActive
    }
  }
`;

interface OverviewFact {
  fact: string;
  date?: string | null;
  publishedDate?: string | null;
  topicId: string;
  topicTitle?: string | null;
  tags: string[];
}
interface Overview {
  articleCount: number;
  atomizedCount: number;
  factCount: number;
  categories: string[];
  suggestions: string[];
  facts: OverviewFact[];
}

interface Source {
  n: number;
  title: string;
  topicId?: string | null;
  url?: string | null;
  facts: number;
}
interface Answer {
  question: string;
  text: string;
  sources: Source[];
  /** Citation marker (C3) → source number. */
  cite: Record<string, number>;
  followups: string[];
  queryId?: string | null;
  feedback?: 'up' | 'down';
  error?: boolean;
}

const FACTS_SHOWN = 12;

const shortDate = (fact: OverviewFact) => {
  const raw = fact.date && /^\d{4}-\d{2}-\d{2}/.test(fact.date) ? fact.date : fact.publishedDate;
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : '';
};

/** One numbered source per article; each citation points at its article. */
function toSources(hits: ResearchHit[]): Pick<Answer, 'sources' | 'cite'> {
  const sources: Source[] = [];
  const cite: Record<string, number> = {};
  for (const h of hits) {
    const key = h.topicId || h.url || h.articleTitle || '';
    let s = sources.find((x) => (x.topicId || x.url || x.title) === key);
    if (!s) {
      s = {
        n: sources.length + 1,
        title: h.articleTitle || h.headline || 'Untitled article',
        topicId: h.topicId,
        url: h.url,
        facts: 0,
      };
      sources.push(s);
    }
    s.facts += 1;
    if (h.citationId) cite[h.citationId.replace(/^C?/, 'C')] = s.n;
  }
  return { sources, cite };
}

function AnswerText({ text, cite }: { text: string; cite: Record<string, number> }) {
  const parts = text.split(/(\[C?\d+\])/g);
  return (
    <p className="rs-answer-text">
      {parts.map((part, i) => {
        const m = part.match(/^\[C?(\d+)\]$/);
        if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
        const n = cite[`C${m[1]}`];
        return n ? (
          <sup key={i} className="rs-cite">
            [{n}]
          </sup>
        ) : null;
      })}
    </p>
  );
}

export function ResearchLayout() {
  const { accountId } = useAccount();
  const client = useApolloClient();
  const [category, setCategory] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [recordFeedback] = useMutation(RECORD_RESEARCH_FEEDBACK);
  const sessionId = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now())
  );
  const sessionSeq = useRef(0);

  const { data: availability, loading: availabilityLoading } = useQuery<{
    researchSettings: { searchApiKey?: string | null; isActive: boolean } | null;
  }>(RESEARCH_AVAILABLE, { variables: { accountId }, skip: !accountId });
  const available = Boolean(
    availability?.researchSettings?.searchApiKey && availability.researchSettings.isActive !== false
  );

  const { data, loading } = useQuery<{ researchOverview: Overview }>(RESEARCH_OVERVIEW, {
    variables: { accountId, category, limit: FACTS_SHOWN },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const overview = data?.researchOverview;
  // Keep the category chips steady while a filtered list loads.
  const [allCategories, setAllCategories] = useState<string[]>([]);
  if (overview && !category && overview.categories.join('|') !== allCategories.join('|')) {
    setAllCategories(overview.categories);
  }

  const ask = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || asking || !accountId) return;
      setInput(q);
      setAsking(true);
      try {
        sessionSeq.current += 1;
        const { data: res } = await client.query<ResearchAskResponse>({
          query: RESEARCH_ASK,
          variables: {
            accountId,
            query: q,
            lang: 'en',
            sessionId: sessionId.current,
            sessionSeq: sessionSeq.current,
          },
          fetchPolicy: 'network-only',
        });
        const r = res.researchAsk;
        const cited = r.citations.length ? r.citations : r.hits;
        setAnswer({
          question: q,
          text: r.answer
            ? r.answer
            : r.hits.length
              ? 'These articles match, but an answer couldn’t be written right now. Try again in a minute.'
              : 'Nothing in your articles’ facts answers that. Try asking it another way.',
          ...toSources(cited),
          followups: r.followups || [],
          queryId: r.answer ? r.queryId : null,
        });
      } catch (err) {
        setAnswer({
          question: q,
          text: err instanceof Error ? err.message : 'That didn’t work. Try again.',
          sources: [],
          cite: {},
          followups: [],
          error: true,
        });
      } finally {
        setAsking(false);
      }
    },
    [accountId, asking, client]
  );

  const feedback = async (value: 'up' | 'down') => {
    if (!answer?.queryId || !accountId) return;
    setAnswer({ ...answer, feedback: value });
    try {
      await recordFeedback({ variables: { accountId, queryId: answer.queryId, feedback: value } });
    } catch {
      // Not worth bothering the person about.
    }
  };

  const chips = useMemo(() => {
    const list = answer?.followups.length ? answer.followups : overview?.suggestions || [];
    return list.slice(0, 3);
  }, [answer, overview]);

  if (!accountId || availabilityLoading) return <div className="rs" />;

  // No facts yet (Prototype v7): one card on what Prism AI does, in place
  // of a question box with nothing to search and an empty Facts list.
  if (overview && !category && overview.factCount === 0) {
    return (
      <div className="rs">
        <div className="rc-empty" style={{ marginTop: 8 }}>
          <div className="rc-empty-title">No facts yet</div>
          <p>
            Prism AI breaks each article into short, sourced facts you can search, ask questions of
            and reuse; they appear here as each article is read.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rs">
      {available ? (
        <>
          <form
            className="rs-ask"
            onSubmit={(e) => {
              e.preventDefault();
              void ask(input);
            }}
          >
            <input
              className="rs-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask your archive a question"
              aria-label="Ask your archive a question"
            />
            <button
              type="submit"
              className="ink-btn ink-btn-primary rs-ask-btn"
              disabled={asking || !input.trim()}
            >
              {asking ? 'Asking…' : 'Ask'}
            </button>
          </form>
          {chips.length ? (
            <div className="rs-chips">
              {chips.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="rs-chip"
                  data-active={answer?.question === q ? 'true' : undefined}
                  disabled={asking}
                  onClick={() => void ask(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          ) : null}

          {answer ? (
            <section className="rs-answer" aria-live="polite">
              <div className="rs-answer-q">{answer.question}</div>
              {answer.error ? (
                <p className="rs-answer-text" style={{ color: 'var(--ink-red)' }}>
                  {answer.text}
                </p>
              ) : (
                <AnswerText text={answer.text} cite={answer.cite} />
              )}
              {answer.sources.length ? (
                <div className="rs-sources">
                  {answer.sources.map((s) => {
                    const body = (
                      <>
                        <span className="rs-source-n">[{s.n}]</span>
                        <span className="rs-source-title">{s.title}</span>
                        <span className="rs-source-count">
                          {s.facts} {s.facts === 1 ? 'fact' : 'facts'}
                        </span>
                      </>
                    );
                    return s.topicId ? (
                      <Link key={s.n} className="rs-source" to={`/content/${s.topicId}`}>
                        {body}
                      </Link>
                    ) : s.url ? (
                      <a
                        key={s.n}
                        className="rs-source"
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {body}
                      </a>
                    ) : (
                      <div key={s.n} className="rs-source">
                        {body}
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="rs-answer-foot">
                <span>Built only from facts in your articles. Nothing here is new reporting.</span>
                {answer.queryId ? (
                  <span className="rs-helpful">
                    {answer.feedback ? (
                      'Thanks'
                    ) : (
                      <>
                        Helpful?
                        <button type="button" onClick={() => void feedback('up')}>
                          Yes
                        </button>
                        <button type="button" onClick={() => void feedback('down')}>
                          No
                        </button>
                      </>
                    )}
                  </span>
                ) : null}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <div className="rc-empty" style={{ marginTop: 8 }}>
          <div className="rc-empty-title">
            {availability?.researchSettings?.isActive === false
              ? 'Research is turned off'
              : 'Research isn’t available yet'}
          </div>
          <p>
            {availability?.researchSettings?.isActive === false
              ? 'Turn it back on in Settings → Research to ask your archive questions.'
              : 'Asking questions isn’t ready on this site yet. Your facts are below.'}
          </p>
        </div>
      )}

      <section className="rs-facts">
        <div className="rs-facts-head">
          <h2 className="rs-h2">Facts</h2>
          {allCategories.length > 1 ? (
            <div className="rs-filters" role="radiogroup" aria-label="Category">
              {[null, ...allCategories].map((c) => (
                <button
                  key={c ?? 'all'}
                  type="button"
                  role="radio"
                  aria-checked={category === c}
                  className="rs-filter"
                  data-active={category === c ? 'true' : undefined}
                  onClick={() => setCategory(c)}
                >
                  {c ?? 'All'}
                </button>
              ))}
            </div>
          ) : null}
          {overview ? (
            <span className="rs-facts-count">
              {overview.atomizedCount} of {overview.articleCount}{' '}
              {overview.articleCount === 1 ? 'article' : 'articles'}{' '}
              {overview.atomizedCount === 1 ? 'has' : 'have'} facts · {overview.factCount}{' '}
              {overview.factCount === 1 ? 'fact' : 'facts'}
            </span>
          ) : null}
        </div>
        {overview?.facts.length ? (
          <div className="rs-fact-list">
            {overview.facts.map((f, i) => (
              <div className="rs-fact" key={`${f.topicId}-${i}`}>
                <div className="rs-fact-body">
                  <div className="rs-fact-text">{f.fact}</div>
                  <div className="rs-fact-meta">
                    {f.tags.map((t) => (
                      <span key={t} className="rs-tag">
                        {t}
                      </span>
                    ))}
                    <Link to={`/content/${f.topicId}`} className="rs-fact-src">
                      {f.topicTitle || 'Untitled article'}
                    </Link>
                  </div>
                </div>
                <span className="rs-fact-date">{shortDate(f)}</span>
              </div>
            ))}
          </div>
        ) : loading && !overview ? null : (
          <p className="rs-none">
            No facts yet. Each article is broken into facts once Prism AI reads it.
          </p>
        )}
      </section>
    </div>
  );
}
